// Copyright (c) Ping. All rights reserved.
//
// Decode budget for the call grid — the single most important lever for keeping
// large calls smooth, especially on Safari. Each rendered <video> = one live
// WebRTC decode + a subscribed track (adaptiveStream only subscribes attached
// tiles). Browsers cap simultaneous hardware video decoders low — Safari very
// low (a handful before it stutters, drops frames, or crashes the tab) — so we
// only ever attach video for a bounded set of REMOTE participants and render
// everyone else as a cheap avatar tile. Suppressed peers stay fully in the call
// (audio + presence); they're just not decoded until they become relevant.
//
// Selection is active-speaker / pin / screen-share prioritised and stable: the
// underlying participant order is identity-sorted, JS sort is stable, so the
// allowed set only changes when priorities actually change — minimising
// subscribe/unsubscribe churn (which is itself expensive on Safari).

import { isSafari } from '@calls/platform';

// Max REMOTE camera streams decoded at once. The local self-view is a local
// capture (no decoder) and is never counted or suppressed. These are the
// CEILINGS for a capable machine; `remoteVideoCap()` scales DOWN on weak ones so
// we get the most tiles a device can actually sustain without stutter — "as many
// as you can render at 60fps, never more". Safari's ceiling is lower because its
// WebRTC decode path is the most constrained engine we ship to.
export const SAFARI_REMOTE_VIDEO_CAP = 9;
export const DEFAULT_REMOTE_VIDEO_CAP = 20;

// Floors so even a misreported / very weak device still shows a usable grid.
const SAFARI_FLOOR = 6;
const DEFAULT_FLOOR = 10;

interface HardwareNavigator {
  hardwareConcurrency?: number;
  deviceMemory?: number; // GiB, Chromium-only (undefined on Safari)
}

/**
 * The live-decode budget for THIS device — raised to the per-browser ceiling on
 * capable hardware and scaled down on constrained machines (few CPU cores / low
 * memory) so we never ask a weak Safari to decode more streams than it can keep
 * smooth. Adaptive = "absolute performance": strong devices show more faces,
 * weak ones stay buttery instead of freezing.
 */
export function remoteVideoCap(): number {
  const safari = isSafari();
  const ceiling = safari ? SAFARI_REMOTE_VIDEO_CAP : DEFAULT_REMOTE_VIDEO_CAP;
  const floor = safari ? SAFARI_FLOOR : DEFAULT_FLOOR;
  const nav: HardwareNavigator =
    typeof navigator !== 'undefined' ? (navigator as HardwareNavigator) : {};
  const cores = nav.hardwareConcurrency ?? 0;
  const mem = nav.deviceMemory;

  let cap = ceiling;
  // Logical-core count is the best cross-browser capability signal (available on
  // Safari). Decode is CPU-bound, so gate on it.
  if (cores > 0 && cores <= 4) cap = Math.min(cap, floor);
  else if (cores > 0 && cores <= 8) cap = Math.min(cap, safari ? 8 : 14);
  // Low device memory (Chromium only) → be extra conservative.
  if (typeof mem === 'number' && mem <= 4) cap = Math.min(cap, 8);
  return Math.max(floor, cap);
}

interface SelectableParticipant {
  readonly identity: string;
  readonly isLocal: boolean;
  readonly isScreenSharing: boolean;
}

/**
 * Identities permitted to render live video this frame. Locals are always
 * included (free). When remotes fit under the cap everyone is allowed (small/
 * medium calls are unaffected). Above the cap we keep the highest-priority
 * remotes — screen-sharers and the pinned participant first, then current
 * speakers, then the stable identity order — so the people you care about are
 * always the ones being decoded.
 */
export function selectVideoIdentities(
  participants: readonly SelectableParticipant[],
  speaking: ReadonlySet<string>,
  pinnedIdentity: string | null,
  cap: number = remoteVideoCap(),
): Set<string> {
  const allowed = new Set<string>();
  const remotes: SelectableParticipant[] = [];
  for (const p of participants) {
    if (p.isLocal) allowed.add(p.identity);
    else remotes.push(p);
  }
  if (remotes.length <= cap) {
    for (const p of remotes) allowed.add(p.identity);
    return allowed;
  }
  const score = (p: SelectableParticipant): number => {
    if (p.isScreenSharing) return 3;
    if (p.identity === pinnedIdentity) return 3;
    if (speaking.has(p.identity)) return 2;
    return 0;
  };
  // Stable sort (input is identity-ordered) → ties keep their order, so the
  // chosen set is deterministic and barely changes between renders.
  const ranked = remotes
    .map((p, i) => ({ p, i, s: score(p) }))
    .sort((a, b) => b.s - a.s || a.i - b.i);
  for (let i = 0; i < cap; i += 1) allowed.add(ranked[i]!.p.identity);
  return allowed;
}
